import { Link, useLocation } from 'react-router-dom';

import { format, parseISO } from 'date-fns';
import { observer } from 'mobx-react-lite';
import styled, { css } from 'styled-components';

import { Button } from '@components/button';
import { IconFilter } from '@components/icons/filter';
import { SearchBox } from '@components/search-box';
import { SelectMulti } from '@components/selectmulti';

import { useEnterpriseFilters } from 'hooks/use-enterprise-filters';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { useStore } from 'models/stores';
import { toSelectedMap } from 'shared/utils/select';

import EmptyStateCard from './EmptyStateCard';
import { getFilteredJobs } from './job-filtering';
import MainHeader from './MainHeader';
import NoAvailableJobs from './NoAvailableJobs';

import { CARD_PADDING, CONTENT_GUTTER, DIVIDER_COLOR } from '../styles/constants';

function JobListEnterprise() {
  const { jobStore, uiStore, companyStore } = useStore();
  const { current } = companyStore;
  const { state, jobs } = jobStore;
  uiStore.clearSelectedJob();
  useDocumentTitle();

  const filters = useEnterpriseFilters();
  const filteredJobs = getFilteredJobs(jobs, filters);

  const jobsByDepartment = filteredJobs.reduce((result, job) => {
    if (result[String(job.department)] === undefined) {
      result[String(job.department)] = [];
    }
    result[String(job.department)].push(job);
    return result;
  }, {});

  const hasJobsWithoutDepartment = filteredJobs.some((job) => job.department === null);

  return (
    <>
      <MainHeader>
        {state === 'loaded' ? (
          <Filters
            hasNotSpecifiedDepartment={jobs.some((job) => job.department === null)}
            filters={filters}
            company={current}
          />
        ) : null}
      </MainHeader>
      {state === 'loaded' && jobs.length === 0 ? (
        <NoAvailableJobs />
      ) : (
        <>
          <OpeningsHeader>
            {state === 'loaded' && jobs.length > 0 && (
              <span>
                {filteredJobs.length < jobs.length
                  ? uiStore.t(
                      `openings_showing_${jobs.length === 1 ? 'singular' : 'plural'}`,
                      filteredJobs.length,
                      jobs.length,
                    )
                  : `${jobs.length} ${uiStore.t(
                      `vacancies_${jobs.length === 1 ? 'singular' : 'plural'}`,
                    )}`}
              </span>
            )}
          </OpeningsHeader>
          {state === 'loaded' && jobs.length > 0 && filteredJobs.length === 0 ? (
            <FilterEmptyState onClear={filters.clearFilters} />
          ) : (
            <JobListContainer>
              {current.departments.map((department) => (
                <JobDepartment
                  department={department.id}
                  jobs={jobsByDepartment[department.id]}
                  key={department.id}
                />
              ))}
              {hasJobsWithoutDepartment && (
                <JobDepartment department={null} jobs={jobsByDepartment[null]} />
              )}
            </JobListContainer>
          )}
        </>
      )}
    </>
  );
}
export default observer(JobListEnterprise);

const JobDepartment = observer(({ jobs, department }) => {
  const { uiStore, companyStore } = useStore();
  const { t } = uiStore;
  if (!jobs) return null;
  const translatedDepartment = companyStore.current.departments.find(
    (d) => d.id === Number(department),
  );
  return (
    <>
      <DepartmentHeader>
        <div>
          {translatedDepartment ? translatedDepartment.translatedName : t('department_unspecified')}
        </div>
        <DeadlineLabel>{t('deadline_display')}</DeadlineLabel>
      </DepartmentHeader>
      {jobs.map((j) => (
        <JobEntry j={j} t={t} key={j.id} />
      ))}
    </>
  );
});

const JobEntry = observer(({ j, t }) => {
  const hasDeadline = Boolean(j.deadline);
  const dl = hasDeadline ? format(parseISO(j.deadline), 'dd.MM.yyyy') : t('deadline_none');
  const search = useLocation().search;
  const jobType = j.status ? t(`jobStatus_${j.status}`) : '';
  const subtitle = [j.locationDisplay, jobType].filter(Boolean).join(' • ');
  return (
    <Link to={{ pathname: String(j.id), search }}>
      <JobRow>
        <JobInfo>
          <JobTitle>{j.title}</JobTitle>
          {subtitle && <JobSubtitle>{subtitle}</JobSubtitle>}
          <JobRowDeadline>{hasDeadline ? `${t('deadline_display')}: ${dl}` : dl}</JobRowDeadline>
        </JobInfo>
        <DeadlineContainer>{dl}</DeadlineContainer>
      </JobRow>
    </Link>
  );
});

const FilterEmptyState = observer(({ onClear }) => {
  const { uiStore } = useStore();
  const { t } = uiStore;
  return (
    <EmptyStateCard
      icon={<IconFilter />}
      heading={t('filter_no_results_main')}
      sub={t('filter_no_results_sub')}
    >
      <Button text={t('clear_all')} onClick={onClear} />
    </EmptyStateCard>
  );
});

const Filters = observer(({ company, filters, hasNotSpecifiedDepartment }) => {
  const { uiStore, companyStore } = useStore();
  const { departmentHeading } = companyStore.current;

  const departmentItems = hasNotSpecifiedDepartment
    ? [
        ...company.selectableDepartments,
        { display: uiStore.t('department_unspecified'), value: 'null' },
      ]
    : company.selectableDepartments;
  const departmentOptions = departmentItems.map((i) => ({
    label: i.display,
    value: String(i.value),
  }));
  const statusOptions = filters.statusTypes.map((i) => ({ label: i.display, value: i.value }));

  return (
    <FilterContainer>
      {company.selectableDepartments.length > 0 && (
        <SelectMulti
          placeholder={departmentHeading}
          options={departmentOptions}
          value={departmentOptions.filter((o) => filters.selectedDepartments.includes(o.value))}
          onChange={(selected) => filters.selectDepartment(toSelectedMap(selected))}
        />
      )}
      <SelectMulti
        placeholder={uiStore.t('jobStatus_title')}
        options={statusOptions}
        value={statusOptions.filter((o) => filters.selectedStatuses.includes(o.value))}
        onChange={(selected) => filters.selectStatus(toSelectedMap(selected))}
      />
      <div id="search">
        <SearchBox
          placeholder={uiStore.t('filter_search_placeholder')}
          value={filters.searchValue}
          onChange={(value) => filters.setSearchValue(value)}
        />
      </div>
    </FilterContainer>
  );
});

const JobListContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  gap: 24px;

  > div {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: ${(p) => p.theme.bp.lg}) {
    gap: 16px;
  }

  @media (max-width: 600px) {
    flex-wrap: wrap;
    gap: 8px;

    #search {
      flex-basis: 100%;
    }
  }

  #search > div > div {
    margin: 0px !important;
  }
`;

const DepartmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  margin: 32px 0 8px;
  padding: 0 ${CONTENT_GUTTER};
  > div:nth-child(1) {
    font-weight: bold;
    color: ${(p) => p.theme.categoryHeaderColor};
  }
`;

const DeadlineLabel = styled.span`
  @media (max-width: 600px) {
    display: none;
  }
`;

const secondaryText = css`
  font-size: 14px;
  font-weight: normal;
  color: ${(p) => p.theme.textColor};
`;

const DeadlineContainer = styled.div`
  flex-shrink: 0;
  ${secondaryText};

  @media (max-width: 600px) {
    display: none;
  }
`;

const JobRowDeadline = styled.div`
  ${secondaryText};
  display: none;

  @media (max-width: 600px) {
    display: block;
  }
`;

const JobInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const JobTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${(p) => p.theme.headingColor};
`;

const JobSubtitle = styled.div`
  ${secondaryText};
`;

const JobRow = styled.div`
  position: relative;
  transition: all 100ms ease-out;
  padding: 32px ${CONTENT_GUTTER};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;

  ::before {
    content: '';
    position: absolute;
    top: 0;
    left: ${CONTENT_GUTTER};
    right: ${CONTENT_GUTTER};
    height: 1px;
    background-color: ${DIVIDER_COLOR};
  }

  :hover {
    background-color: ${(p) => p.theme.listBgHoverColor};
    cursor: pointer;

    ${JobTitle} {
      color: ${(p) => p.theme.headingHoverColor};
    }
  }

  @media (max-width: 600px) {
    padding: ${CARD_PADDING} ${CONTENT_GUTTER};
  }
`;

const OpeningsHeader = styled.h2`
  color: ${(p) => p.theme.headingColor};
  font-weight: bold;
  font-size: 18px;
  margin-top: 24px;
  margin-bottom: 0px;
  padding: 0 ${CONTENT_GUTTER};
`;
